import React, { useEffect, useMemo, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { useLang } from '@/context/LanguageContext';
import { useServices } from '@/context/ServicesContext';
import { Icon } from '@/components/icons';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Check, ArrowLeft, ArrowRight, Loader2, CheckCircle2 } from 'lucide-react';

const STEP_COUNT = 4;

export default function OfferWizard({ open, onOpenChange, prefillService = '' }) {
  const { t, tv, lang } = useLang();
  const { services } = useServices();

  const emptyForm = { service: '', scope: [], budget: '', timeline: '', name: '', company: '', email: '', phone: '', message: '',
    device_type: '', manufacturer: '', model: '', serial_number: '', powers_on: 'yes', urgency: '', preferred_date: '', error_description: '' };
  const [form, setForm] = useState(emptyForm);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const isRepair = form.service === 'geraete-reparatur';
  const R = t('repair.form');

  useEffect(() => {
    if (open) {
      setForm({ ...emptyForm, service: prefillService });
      setStep(prefillService ? 1 : 0);
      setDone(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, prefillService]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleScope = (opt) => setForm((f) => ({
    ...f,
    scope: f.scope.includes(opt) ? f.scope.filter((x) => x !== opt) : [...f.scope, opt],
  }));

  const stepTitles = [
    { title: t('wizard.stepServiceTitle'), sub: t('wizard.stepServiceSubtitle') },
    isRepair
      ? { title: R.deviceSection, sub: R.subtitle }
      : { title: t('wizard.stepScopeTitle'), sub: t('wizard.stepScopeSubtitle') },
    isRepair
      ? { title: R.urgency + ' & ' + R.preferredDate, sub: t('wizard.stepBudgetSubtitle') }
      : { title: t('wizard.stepBudgetTitle'), sub: t('wizard.stepBudgetSubtitle') },
    { title: t('wizard.stepContactTitle'), sub: t('wizard.stepContactSubtitle') },
  ];

  const activeService = useMemo(() => services.find((s) => s.slug === form.service), [services, form.service]);
  const accent = activeService?.accent || 'var(--itw-cyan)';

  const canNext = () => {
    if (step === 0) return !!form.service;
    return true;
  };

  const next = () => {
    if (step === 0 && !form.service) { toast.error(t('wizard.selectServiceFirst')); return; }
    setStep((s) => Math.min(s + 1, STEP_COUNT - 1));
  };
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const submit = async () => {
    if (isRepair && (!form.device_type || !form.error_description)) { toast.error(R.errorRequired); return; }
    if (!form.name || !form.email) { toast.error(t('wizard.contactError')); return; }
    setSubmitting(true);
    try {
      if (isRepair) {
        const summaryParts = [
          form.urgency ? `${R.urgency}: ${form.urgency}` : '',
          form.preferred_date ? `${R.preferredDate}: ${form.preferred_date}` : '',
        ].filter(Boolean);
        const message = [form.message, summaryParts.join(' · ')].filter(Boolean).join('\n\n');
        await api.post('/leads', {
          name: form.name, company: form.company, email: form.email, phone: form.phone,
          service: 'geraete-reparatur', type: 'repair',
          device_type: form.device_type, manufacturer: form.manufacturer, model: form.model,
          serial_number: form.serial_number, powers_on: form.powers_on, urgency: form.urgency,
          preferred_date: form.preferred_date, error_description: form.error_description,
          message, language: lang, source_page: window.location.pathname,
        });
      } else {
        const summaryParts = [
          activeService ? `${t('offer.service')}: ${tv(activeService.name)}` : '',
          form.scope.length ? `Scope: ${form.scope.join(', ')}` : '',
          form.budget ? `${t('wizard.budgetLabel')}: ${form.budget}` : '',
          form.timeline ? `${t('wizard.timelineLabel')}: ${form.timeline}` : '',
        ].filter(Boolean);
        const message = [form.message, summaryParts.join(' · ')].filter(Boolean).join('\n\n');
        await api.post('/leads', {
          name: form.name, company: form.company, email: form.email, phone: form.phone,
          service: form.service, budget: form.budget, timeline: form.timeline, scope: form.scope,
          message, language: lang, type: 'offer', source_page: window.location.pathname,
        });
      }
      setDone(true);
      toast.success(t('wizard.successTitle'));
    } catch (err) {
      toast.error(t('offer.error'));
    } finally {
      setSubmitting(false);
    }
  };

  const pill = (selected) => `rounded-full px-4 py-2.5 text-sm font-medium border transition-[background-color,border-color,color,box-shadow] duration-200 text-left ${selected
    ? 'text-[color:var(--itw-bg)]'
    : 'text-[color:var(--itw-text-2)] border-[color:var(--itw-border)] hover:border-[color:var(--itw-border-strong)] bg-[color:var(--itw-surface-2)]'}`;
  const pillStyle = (selected) => (selected ? { backgroundColor: accent, borderColor: accent, boxShadow: `0 0 20px ${accent}55` } : {});

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="glass border-[color:var(--itw-border-strong)] sm:max-w-[620px] max-h-[92vh] overflow-y-auto" data-testid="offer-wizard">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-[color:var(--itw-text)]">{t('wizard.title')}</DialogTitle>
          <DialogDescription className="text-[color:var(--itw-text-2)]">{t('wizard.subtitle')}</DialogDescription>
        </DialogHeader>

        {done ? (
          <div className="flex flex-col items-center text-center py-12" data-testid="offer-wizard-success">
            <div className="h-16 w-16 rounded-full grid place-items-center bg-[color:var(--itw-cyan)]/12 border border-[color:var(--itw-border-strong)]">
              <CheckCircle2 className="h-9 w-9 text-[color:var(--itw-cyan)]" />
            </div>
            <p className="mt-5 text-[color:var(--itw-text)] max-w-sm">{t('wizard.success')}</p>
            <Button className="mt-6 rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] neon-edge hover:opacity-90 transition-opacity"
              onClick={() => onOpenChange(false)} data-testid="offer-wizard-close-button">OK</Button>
          </div>
        ) : (
          <>
            {/* progress */}
            <div className="mt-1">
              <div className="flex items-center justify-between font-mono text-[11px] text-[color:var(--itw-text-3)] mb-2">
                <span>{t('wizard.step')} {step + 1} {t('wizard.of')} {STEP_COUNT}</span>
                <span>{Math.round(((step + 1) / STEP_COUNT) * 100)}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-[color:var(--itw-surface-2)] overflow-hidden border border-[color:var(--itw-border)]">
                <motion.div className="h-full rounded-full" initial={false}
                  animate={{ width: `${((step + 1) / STEP_COUNT) * 100}%` }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  style={{ background: 'linear-gradient(90deg,#1452A6,#00E7FF)' }} />
              </div>
            </div>

            <div className="mt-5">
              <h3 className="text-lg font-semibold text-[color:var(--itw-text)]">{stepTitles[step].title}</h3>
              <p className="text-sm text-[color:var(--itw-text-3)]">{stepTitles[step].sub}</p>
            </div>

            <div className="mt-4 min-h-[240px]">
              <AnimatePresence mode="wait">
                {/* STEP 0 — SERVICE */}
                {step === 0 && (
                  <motion.div key="s0" data-testid="offer-wizard-step-0"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="grid grid-cols-2 gap-2.5">
                    {services.map((s) => {
                      const sel = form.service === s.slug;
                      return (
                        <button key={s.slug} type="button" onClick={() => set('service', s.slug)}
                          data-testid={`offer-wizard-service-${s.slug}`}
                          className={`flex items-center gap-2.5 rounded-xl border p-3 text-left transition-[background-color,border-color,box-shadow] duration-200 ${sel ? '' : 'border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)] hover:border-[color:var(--itw-border-strong)]'}`}
                          style={sel ? { borderColor: s.accent, boxShadow: `0 0 18px ${s.accent}44`, background: `${s.accent}12` } : {}}>
                          <span className="h-8 w-8 shrink-0 rounded-lg grid place-items-center border border-[color:var(--itw-border)]" style={{ background: 'var(--itw-surface)' }}>
                            <Icon name={s.icon} className="h-4 w-4" style={{ color: s.accent }} />
                          </span>
                          <span className="text-sm text-[color:var(--itw-text)] leading-tight">{tv(s.name)}</span>
                          {sel && <Check className="h-4 w-4 ml-auto shrink-0" style={{ color: s.accent }} />}
                        </button>
                      );
                    })}
                  </motion.div>
                )}

                {/* STEP 1 — SCOPE (non-repair) */}
                {step === 1 && !isRepair && (
                  <motion.div key="s1" data-testid="offer-wizard-step-1"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                    {t('wizard.scopeOptions').map((opt, i) => {
                      const sel = form.scope.includes(opt);
                      return (
                        <button key={i} type="button" onClick={() => toggleScope(opt)}
                          data-testid={`offer-wizard-scope-${i}`}
                          className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-[background-color,border-color] duration-200 ${sel ? 'border-[color:var(--itw-border-strong)] bg-[color:var(--itw-cyan)]/10' : 'border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)] hover:border-[color:var(--itw-border-strong)]'}`}>
                          <span className={`h-5 w-5 shrink-0 rounded-md grid place-items-center border ${sel ? 'bg-[color:var(--itw-cyan)] border-[color:var(--itw-cyan)]' : 'border-[color:var(--itw-border-strong)]'}`}>
                            {sel && <Check className="h-3.5 w-3.5 text-[color:var(--itw-bg)]" />}
                          </span>
                          <span className="text-sm text-[color:var(--itw-text)]">{opt}</span>
                        </button>
                      );
                    })}
                  </motion.div>
                )}

                {/* STEP 1 — DEVICE (repair) */}
                {step === 1 && isRepair && (
                  <motion.div key="s1r" data-testid="offer-wizard-step-1-repair"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="space-y-3">
                    <div className="space-y-1.5">
                      <Label>{R.deviceType} *</Label>
                      <Select value={form.device_type} onValueChange={(v) => set('device_type', v)}>
                        <SelectTrigger data-testid="wizard-repair-device-type" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]"><SelectValue placeholder={R.deviceTypePlaceholder} /></SelectTrigger>
                        <SelectContent>{R.deviceTypes.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-1.5"><Label>{R.manufacturer}</Label><Input value={form.manufacturer} onChange={(e) => set('manufacturer', e.target.value)} data-testid="wizard-repair-manufacturer" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                      <div className="space-y-1.5"><Label>{R.model}</Label><Input value={form.model} onChange={(e) => set('model', e.target.value)} data-testid="wizard-repair-model" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                    </div>
                    <div className="space-y-2">
                      <Label>{R.powersOn}</Label>
                      <RadioGroup value={form.powers_on} onValueChange={(v) => set('powers_on', v)} className="flex flex-wrap gap-2">
                        {[['yes', R.powersYes], ['no', R.powersNo], ['unknown', R.powersUnknown]].map(([val, label]) => {
                          const sel = form.powers_on === val;
                          return (
                            <label key={val} className={`flex items-center gap-2 rounded-full border px-3.5 py-1.5 text-sm cursor-pointer transition-colors ${sel ? 'border-[color:var(--itw-cyan)] bg-[color:var(--itw-cyan)]/10 text-[color:var(--itw-text)]' : 'border-[color:var(--itw-border)] text-[color:var(--itw-text-2)]'}`}>
                              <RadioGroupItem value={val} className="border-[color:var(--itw-border-strong)] text-[color:var(--itw-cyan)]" />{label}
                            </label>
                          );
                        })}
                      </RadioGroup>
                    </div>
                    <div className="space-y-1.5"><Label>{R.errorDescription} *</Label><Textarea rows={3} value={form.error_description} onChange={(e) => set('error_description', e.target.value)} placeholder={R.errorPlaceholder} data-testid="wizard-repair-error" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" /></div>
                  </motion.div>
                )}

                {/* STEP 2 — BUDGET + TIMELINE (non-repair) */}
                {step === 2 && !isRepair && (
                  <motion.div key="s2" data-testid="offer-wizard-step-2"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="space-y-6">
                    <div>
                      <Label className="text-[color:var(--itw-text-2)]">{t('wizard.budgetLabel')}</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {t('offer.budgetRanges').map((b, i) => (
                          <button key={i} type="button" onClick={() => set('budget', b)} data-testid={`offer-wizard-budget-${i}`}
                            className={pill(form.budget === b)} style={pillStyle(form.budget === b)}>{b}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-[color:var(--itw-text-2)]">{t('wizard.timelineLabel')}</Label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {t('wizard.timelines').map((tl, i) => (
                          <button key={i} type="button" onClick={() => set('timeline', tl)} data-testid={`offer-wizard-timeline-${i}`}
                            className={pill(form.timeline === tl)} style={pillStyle(form.timeline === tl)}>{tl}</button>
                        ))}
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* STEP 2 — URGENCY + DATE (repair) */}
                {step === 2 && isRepair && (
                  <motion.div key="s2r" data-testid="offer-wizard-step-2-repair"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="space-y-5">
                    <div className="space-y-1.5">
                      <Label className="text-[color:var(--itw-text-2)]">{R.urgency}</Label>
                      <Select value={form.urgency} onValueChange={(v) => set('urgency', v)}>
                        <SelectTrigger data-testid="wizard-repair-urgency" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]"><SelectValue placeholder={R.urgencyPlaceholder} /></SelectTrigger>
                        <SelectContent>{R.urgencyOptions.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[color:var(--itw-text-2)]">{R.preferredDate}</Label>
                      <Input type="date" value={form.preferred_date} onChange={(e) => set('preferred_date', e.target.value)} data-testid="wizard-repair-date" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-[color:var(--itw-text-2)]">{R.serial}</Label>
                      <Input value={form.serial_number} onChange={(e) => set('serial_number', e.target.value)} placeholder={R.serialPlaceholder} data-testid="wizard-repair-serial" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                    </div>
                  </motion.div>
                )}

                {/* STEP 3 — CONTACT */}
                {step === 3 && (
                  <motion.div key="s3" data-testid="offer-wizard-step-3"
                    initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }} transition={{ duration: 0.22 }}
                    className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label>{t('wizard.name')} *</Label>
                        <Input value={form.name} onChange={(e) => set('name', e.target.value)} data-testid="offer-wizard-name-input" required className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{t('wizard.company')}</Label>
                        <Input value={form.company} onChange={(e) => set('company', e.target.value)} data-testid="offer-wizard-company-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{t('wizard.email')} *</Label>
                        <Input type="email" value={form.email} onChange={(e) => set('email', e.target.value)} data-testid="offer-wizard-email-input" required className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                      </div>
                      <div className="space-y-1.5">
                        <Label>{t('wizard.phone')}</Label>
                        <Input value={form.phone} onChange={(e) => set('phone', e.target.value)} data-testid="offer-wizard-phone-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                      </div>
                    </div>
                    <div className="space-y-1.5">
                      <Label>{t('wizard.message')}</Label>
                      <Textarea value={form.message} onChange={(e) => set('message', e.target.value)} rows={3} data-testid="offer-wizard-message-input" className="bg-[color:var(--itw-surface-2)] border-[color:var(--itw-border)]" />
                    </div>
                    {/* summary */}
                    <div className="rounded-xl border border-[color:var(--itw-border)] bg-[color:var(--itw-surface-2)] p-3">
                      <div className="text-[11px] uppercase tracking-wider text-[color:var(--itw-text-3)] font-mono mb-2">{t('wizard.summary')}</div>
                      <div className="flex flex-wrap gap-1.5">
                        {activeService && <span className="rounded-full px-2.5 py-1 text-[11px]" style={{ color: accent, background: `${accent}18` }}>{tv(activeService.name)}</span>}
                        {form.budget && <span className="rounded-full px-2.5 py-1 text-[11px] text-[color:var(--itw-text-2)] bg-[color:var(--itw-surface)]">{form.budget}</span>}
                        {form.timeline && <span className="rounded-full px-2.5 py-1 text-[11px] text-[color:var(--itw-text-2)] bg-[color:var(--itw-surface)]">{form.timeline}</span>}
                        {form.scope.map((sc) => <span key={sc} className="rounded-full px-2.5 py-1 text-[11px] text-[color:var(--itw-text-2)] bg-[color:var(--itw-surface)]">{sc}</span>)}
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* footer nav */}
            <div className="mt-2 flex items-center justify-between gap-3">
              <Button type="button" variant="ghost" onClick={back} disabled={step === 0}
                data-testid="offer-wizard-back"
                className="text-[color:var(--itw-text-2)] hover:text-[color:var(--itw-text)] disabled:opacity-30">
                <ArrowLeft className="h-4 w-4 mr-1.5" /> {t('wizard.back')}
              </Button>
              {step < STEP_COUNT - 1 ? (
                <Button type="button" onClick={next} disabled={!canNext()}
                  data-testid="offer-wizard-next"
                  className="rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity disabled:opacity-40">
                  {t('wizard.next')} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Button>
              ) : (
                <Button type="button" onClick={submit} disabled={submitting}
                  data-testid="offer-wizard-submit"
                  className="rounded-full bg-[color:var(--itw-cyan)] text-[color:var(--itw-bg)] font-medium neon-edge hover:opacity-90 transition-opacity">
                  {submitting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{t('wizard.sending')}</> : t('wizard.submit')}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
